import React, { useState, useEffect, useRef } from 'react';
import { Activity, X, Sparkles, Crown, List, ChevronDown } from 'lucide-react';
import { NATURES, INGREDIENT_ICONS, SUBSKILL_ABBREVIATIONS, getSkillColor, BERRY_ICONS, MAIN_SKILL_ICONS, formatSkillName } from '../utils/gameData';
import { getToxelEvolution, calculateProduction, SKILL_GROUPS, INGREDIENT_MAP } from '../utils/pokemonMaths';

const CATEGORIES = ['Berry', 'Ingredient', 'Skill'];

const TARGETS = {
  Berry: ['Belue', 'Bluk', 'Cheri', 'Chesto', 'Durin', 'Figy', 'Grepa', 'Leppa', 'Lum', 'Mago', 'Oran', 'Pamtre', 'Pecha', 'Persim', 'Rawst', 'Sitrus', 'Wiki', 'Yache'],
  Ingredient: ['Apple', 'Avocado', 'Cacao', 'Coffee', 'Corn', 'Egg', 'Ginger', 'Herb', 'Honey', 'Leek', 'Milk', 'Mushroom', 'Oil', 'Potato', 'Pumpkin', 'Sausage', 'Soybean', 'Tail', 'Tomato'],
  Skill: [
    'Berry Burst', 'Charge Energy S', 'Charge Strength M', 'Charge Strength S', 
    'Cooking Assist S', 'Cooking Power-Up S', 'Dream Shard Magnet S', 'Energizing Cheer S', 
    'Energy for Everyone', 'Extra Helpful S', 'Helper Boost', 'Ingredient Draw S', 
    'Ingredient Magnet S', 'Metronome', 'Skill Copy', 'Tasty Chance S', 'Versatile'
  ]
};

const SKILL_ICON_KEYS = {
  'Berry Burst': 'BerryBurst',
  'Charge Energy S': 'ChargeEnergyS',
  'Charge Strength M': 'ChargeStrengthM',
  'Charge Strength S': 'ChargeStrengthS',
  'Cooking Assist S': 'CookingAssistS',
  'Cooking Power-Up S': 'CookingPowerUpS',
  'Dream Shard Magnet S': 'DreamShardMagnetS',
  'Energizing Cheer S': 'EnergizingCheerS',
  'Energy for Everyone': 'EnergyForEveryone',
  'Extra Helpful S': 'ExtraHelpfulS',
  'Helper Boost': 'HelperBoost',
  'Ingredient Draw S': 'IngredientDrawS',
  'Ingredient Magnet S': 'IngredientMagnetS',
  'Metronome': 'Metronome',
  'Skill Copy': 'SkillCopyMimic',
  'Tasty Chance S': 'TastyChanceS',
  'Versatile': 'Versatile'
};

export default function BenchmarkModal({ isOpen, onClose, pokedex, box = [], selectedMons = [] }) {
  const [mode, setMode] = useState('all'); 
  const [category, setCategory] = useState('Berry');
  const [target, setTarget] = useState(TARGETS['Berry'][0]);
  
  const [selectedBoxMons, setSelectedBoxMons] = useState([]);
  const [isBoxSelectOpen, setIsBoxSelectOpen] = useState(false);
  const boxSelectRef = useRef(null);
  
  const [boxSortBy, setBoxSortBy] = useState('levelDesc');
  const [baselineLevel, setBaselineLevel] = useState(30);
  const [isGodRoll, setIsGodRoll] = useState(false);
  const [ignoreSkills, setIgnoreSkills] = useState(false);
  const [scaleToBaseline, setScaleToBaseline] = useState(false);
  const [showTiers, setShowTiers] = useState(false);
  
  const [energyStrategy, setEnergyStrategy] = useState('natural');
  const [inventoryMode, setInventoryMode] = useState('unlimited');

  // NEW: Evolution State
  const [simulateEvolution, setSimulateEvolution] = useState(false);
  const [evolutionTarget, setEvolutionTarget] = useState('');
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);

  const [results, setResults] = useState([]); 

  // Use the first selected mon for UI target suggestions, calculation engine handles the full array
  const selectedMonData = selectedBoxMons.length > 0 ? box.find(m => m.uuid === selectedBoxMons[0]) : null;
  const selectedSpecies = selectedMonData && pokedex ? pokedex[selectedMonData.species_id] : null;

  // Track if current mon can evolve and what it can turn into
  const canEvolve = selectedSpecies && selectedSpecies.id !== selectedSpecies.final_evolution;
  const possibleEvolutions = React.useMemo(() => {
      if (!selectedSpecies) return [];
      const family = selectedSpecies.family;
      
      if (selectedSpecies.id === 'toxel' && selectedMonData) {
          return [pokedex[getToxelEvolution(selectedMonData.nature)]].filter(Boolean);
      }

      // Fetch all pokemon that share this family and are fully evolved (id matches final_evolution)
      return Object.values(pokedex).filter(mon => mon.family === family && mon.id === mon.final_evolution);
  }, [selectedSpecies, pokedex, selectedMonData]);

  // Extract unique berries, ingredients, and skills from all selected Pokémon
  const uniqueTargets = React.useMemo(() => {
      const berries = new Set();
      const allIngredients = new Set();
      const unlockedIngredients = new Set();
      const skills = new Set();

      selectedBoxMons.forEach(uuid => {
          const mon = box.find(m => m.uuid === uuid);
          if (mon && pokedex) {
              const species = pokedex[mon.species_id];
              if (species) {
                  if (species.berry) berries.add(species.berry.toUpperCase());
                  if (mon.ingredients) {
                      ["0", "30", "60"].forEach(lvl => {
                          const ing = mon.ingredients[lvl];
                          if (ing) {
                              allIngredients.add(ing.name);
                              if (lvl === "0" || mon.level >= parseInt(lvl)) {
                                  unlockedIngredients.add(ing.name);
                              }
                          }
                      });
                  }
                  
                  const activeSkill = mon.custom_skill || species.skill;
                  if (activeSkill) {
                      skills.add(activeSkill);
                  }
              }
          }
      });

      return {
          berries: Array.from(berries),
          ingredients: Array.from(allIngredients).map(name => ({
              name,
              isLocked: !unlockedIngredients.has(name)
          })),
          skills: Array.from(skills)
      };
  }, [selectedBoxMons, box, pokedex]);

  // Sync the external App selection with the local modal state when opened
  useEffect(() => {
    if (isOpen) {
      if (selectedMons && selectedMons.length > 0) {
        setSelectedBoxMons(selectedMons);
        // Optional: If they selected multiple, you might want to default to 'box' mode
        if (selectedMons.length > 1) setMode('box');
      } else {
        setSelectedBoxMons([]); // Reset if opened with no active selection
      }
    }
  }, [isOpen, selectedMons]);

  // Reset to default final evo if you switch your Box Mon
  useEffect(() => {
      if (selectedSpecies && selectedSpecies.id !== selectedSpecies.final_evolution) {
          if (selectedSpecies.id === 'toxel' && selectedMonData) {
              setEvolutionTarget(getToxelEvolution(selectedMonData.nature));
          } else {
              setEvolutionTarget(selectedSpecies.final_evolution);
          }
      } else {
          setEvolutionTarget('');
      }
  }, [selectedSpecies, selectedMonData]);

  // UPDATE: Pass evolution settings to math engine
  useEffect(() => {
    if (!isOpen || !pokedex || Object.keys(pokedex).length === 0) return;

    const resultsList = [];
    
    const evaluateBoxMon = (boxMon, isHighlighted) => {
      const baseSpecies = pokedex[boxMon?.species_id];
      if (!baseSpecies) return null;

      let speciesToEvaluate = baseSpecies;
      if (simulateEvolution && baseSpecies.id !== baseSpecies.final_evolution) {
        if (isHighlighted && evolutionTarget && pokedex[evolutionTarget]?.family === baseSpecies.family) {
          speciesToEvaluate = pokedex[evolutionTarget] || pokedex[baseSpecies.final_evolution];
        } else if (baseSpecies.id === 'toxel') {
          speciesToEvaluate = pokedex[getToxelEvolution(boxMon.nature)] || baseSpecies;
        } else {
          speciesToEvaluate = pokedex[baseSpecies.final_evolution];
        }
        if (!speciesToEvaluate) speciesToEvaluate = baseSpecies;
      }

      const evalLevel = scaleToBaseline ? baselineLevel : boxMon.level;
      const mockMon = {
        ...boxMon,
        level: evalLevel,
        nature: ignoreSkills ? 'Serious' : (boxMon.nature || 'Serious'),
        subskills: ignoreSkills ? [] : boxMon.subskills
      };

      const prod = calculateProduction(speciesToEvaluate, mockMon, 24, { energyStrategy, inventoryMode });

      let production = 0;
      let valid = false;

      if (category === 'Berry' && speciesToEvaluate.berry === target.toUpperCase()) {
        valid = true;
        production = prod.berries;
      } else if (category === 'Ingredient') {
        const mappedTarget = INGREDIENT_MAP[target];
        const ingAmt = prod.ingredients[mappedTarget] || 0;
        if (ingAmt > 0) {
          valid = true;
          production = ingAmt;
        }
      } else if (category === 'Skill') {
        const activeSkill = boxMon.custom_skill || speciesToEvaluate.skill;
        const keywords = SKILL_GROUPS[target] || [target];
        const skillStr = Array.isArray(activeSkill) ? activeSkill.join(' ') : (activeSkill || '');
        if (keywords.some(kw => skillStr.toLowerCase().includes(kw.toLowerCase()))) {
          valid = true;
          production = parseFloat(prod.skills);
        }
      }

      if (!valid && !isHighlighted) return null;

      const baseName = boxMon.nickname ? `${baseSpecies.name} (${boxMon.nickname})` : baseSpecies.name;
      const finalName = (simulateEvolution && speciesToEvaluate.id !== baseSpecies.id)
        ? `${baseName} ➡️ ${speciesToEvaluate.name}`
        : baseName;

      return {
        uniqueKey: `injected-${boxMon.uuid}`,
        name: finalName,
        id: speciesToEvaluate.id,
        is_shiny: boxMon.is_shiny,
        production: valid ? production : 0,
        isInjected: isHighlighted,
        display: {
          level: evalLevel,
          isScaled: scaleToBaseline,
          nature: mockMon.nature,
          subskills: ignoreSkills ? [null, null, null, null, null] : boxMon.subskills,
          ingredients: boxMon.ingredients,
          label: valid ? (ignoreSkills ? "Ignored Skills" : "") : `Does not produce ${target}`,
          isValid: valid,
          forceUnlock: false
        }
      };
    };

    if (mode === 'all') {
      for (const key in pokedex) {
        const mon = pokedex[key];

        let mockSubskills = [];
        let mockNature = "Serious";
        let displaySubskills = [null, null, null, null, null];
        const displayLabel = isGodRoll ? "Perfect Stats" : "Base Stats";

        if (isGodRoll) {
          if (category === 'Berry') {
            mockNature = "Jolly";
            mockSubskills = ["Berry Finding S", "Helping Speed M", "Helping Bonus"];
            displaySubskills = ["Berry Finding S", "Helping Speed M", "Helping Bonus", null, null];
          } else if (category === 'Ingredient') {
            mockNature = "Quiet";
            mockSubskills = ["Ingredient Finder M", "Ingredient Finder S", "Helping Speed M"];
            displaySubskills = ["Ingredient Finder M", "Ingredient Finder S", "Helping Speed M", null, null];
          } else if (category === 'Skill') {
            mockNature = "Careful";
            mockSubskills = ["Skill Trigger M", "Skill Trigger S", "Helping Speed M"];
            displaySubskills = ["Skill Trigger M", "Skill Trigger S", "Helping Speed M", null, null];
          }
        }

        const mockIngredients = {};
        const slotKeys = ['0'];
        if (baselineLevel >= 30) slotKeys.push('30');
        if (baselineLevel >= 60) slotKeys.push('60');

        slotKeys.forEach(slotKey => {
          const pool = mon.ingredientPools?.[slotKey];
          if (!pool || pool.length === 0) return;
          if (category === 'Ingredient') {
            const mappedTarget = INGREDIENT_MAP[target];
            mockIngredients[slotKey] = pool.find(i => i.name === mappedTarget) || pool[0];
          } else {
            mockIngredients[slotKey] = pool[0];
          }
        });

        let finalSpecies = mon;
        if (simulateEvolution && mon.id !== mon.final_evolution) {
            let targetEvoId = mon.final_evolution;
            if (mon.id === 'toxel') {
                targetEvoId = getToxelEvolution(mockNature);
            }
            if (pokedex[targetEvoId]) {
                finalSpecies = pokedex[targetEvoId];
            }
        }

        const mockMon = {
          level: baselineLevel,
          nature: mockNature,
          subskills: mockSubskills,
          ingredients: mockIngredients,
          custom_skill: null
        };

        const prod = calculateProduction(finalSpecies, mockMon, 24, { energyStrategy, inventoryMode });

        let production = 0;
        let valid = false;

        if (category === 'Berry') {
          if (finalSpecies.berry === target.toUpperCase()) {
            valid = true;
            production = prod.berries;
          }
        } else if (category === 'Ingredient') {
          const mappedTarget = INGREDIENT_MAP[target];
          const ingAmt = prod.ingredients[mappedTarget] || 0;
          if (ingAmt > 0) {
            valid = true;
            production = ingAmt;
          }
        } else if (category === 'Skill') {
          const keywords = SKILL_GROUPS[target] || [target];
          const skillStr = Array.isArray(finalSpecies.skill) ? finalSpecies.skill.join(' ') : (finalSpecies.skill || '');
          if (keywords.some(kw => skillStr.toLowerCase().includes(kw.toLowerCase()))) {
            valid = true;
            production = parseFloat(prod.skills);
          }
        }

        if (valid) {
          resultsList.push({
            uniqueKey: finalSpecies.id,
            name: simulateEvolution && finalSpecies.id !== mon.id ? `${mon.name} ➡️ ${finalSpecies.name}` : mon.name,
            id: finalSpecies.id,
            is_shiny: false,
            production,
            isInjected: false,
            display: {
              level: baselineLevel,
              nature: mockNature,
              subskills: displaySubskills,
              label: displayLabel,
              isValid: true,
              forceUnlock: true
            }
          });
        }
      }

      if (selectedBoxMons && selectedBoxMons.length > 0) {
        selectedBoxMons.forEach(uuid => {
          const boxMon = box.find(m => m.uuid === uuid);
          if (boxMon) {
            const res = evaluateBoxMon(boxMon, true);
            if (res) resultsList.push(res);
          }
        });
      }
    } else if (mode === 'box') {
      box.forEach(boxMon => {
        const isHighlighted = selectedBoxMons.includes(boxMon.uuid);
        const res = evaluateBoxMon(boxMon, isHighlighted);
        if (res) resultsList.push(res);
      });
    }

    resultsList.sort((a, b) => b.production - a.production);
    setResults(resultsList);
  }, [category, target, isOpen, pokedex, box, selectedBoxMons, baselineLevel, isGodRoll, ignoreSkills, scaleToBaseline, mode, simulateEvolution, evolutionTarget, energyStrategy, inventoryMode]);

  // Handle outside click for the multiselect dropdown
  useEffect(() => {
    function handleClickOutside(event) {
      if (boxSelectRef.current && !boxSelectRef.current.contains(event.target)) {
        setIsBoxSelectOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const getCategoryIcon = (cat) => {
      switch(cat) {
          case 'Berry': return "/images/berries/berries.png";
          case 'Ingredient': return "/images/ingredient/ingredients.png";
          case 'Skill': return "/images/mainskill/metronome.png"; 
          default: return null;
      }
  };

  const getTargetIcon = (cat, tgt) => {
      if (!tgt) return null;
      if (cat === 'Berry') return BERRY_ICONS[tgt.toUpperCase()];
      if (cat === 'Ingredient') return INGREDIENT_ICONS[INGREDIENT_MAP[tgt]];
      if (cat === 'Skill') return MAIN_SKILL_ICONS[SKILL_ICON_KEYS[tgt]];
      return null;
  };

  const formatBerryTarget = (berry) => berry.charAt(0).toUpperCase() + berry.slice(1).toLowerCase();
  
  const getTargetForIngredient = (ingName) => {
      const entry = Object.entries(INGREDIENT_MAP).find(([k, v]) => v === ingName);
      return entry ? entry[0] : null;
  };
  
  const getTargetForSkill = (rawSkill) => {
      const skillStr = Array.isArray(rawSkill) ? rawSkill.join(' ') : (rawSkill || '');
      for (const [group, keywords] of Object.entries(SKILL_GROUPS)) {
          if (keywords.some(kw => skillStr.toLowerCase().includes(kw.toLowerCase()))) {
              return group;
          }
      }
      return null;
  };

  const renderSubskills = (subskills, level, forceUnlock) => {
    const unlockLevels = [10, 25, 50, 75, 100];
    return (
      <div className="flex gap-1 mt-1">
        {subskills.map((skill, idx) => {
          if (!skill) return <div key={idx} className="w-8 h-4 bg-gray-50 rounded border border-gray-100" />;
          const isUnlocked = forceUnlock || level >= unlockLevels[idx];
          const abbr = SUBSKILL_ABBREVIATIONS[skill] || skill.slice(0,2);
          const colorClass = getSkillColor(skill);
          
          return (
              <div key={idx} className={`w-7 md:w-8 h-4 flex items-center justify-center rounded-[3px] border text-[7px] md:text-[8px] font-black tracking-tighter ${colorClass} ${!isUnlocked ? 'opacity-60 saturate-50' : ''}`} title={skill}>
                  {abbr}
              </div>
          );
        })}
      </div>
    );
  };

  const boxWithIndex = box.map((mon, index) => ({ ...mon, originalIndex: index }));
  const sortedBox = [...boxWithIndex].sort((a, b) => {
      const speciesA = pokedex[a.species_id]?.name || '';
      const speciesB = pokedex[b.species_id]?.name || '';
      const timeA = a.date_met ? new Date(a.date_met).getTime() : a.originalIndex;
      const timeB = b.date_met ? new Date(b.date_met).getTime() : b.originalIndex;
      
      if (boxSortBy === 'levelDesc') return b.level - a.level || speciesA.localeCompare(speciesB);
      if (boxSortBy === 'levelAsc') return a.level - b.level || speciesA.localeCompare(speciesB);
      if (boxSortBy === 'nameAsc') return speciesA.localeCompare(speciesB) || b.level - a.level;
      if (boxSortBy === 'nameDesc') return speciesB.localeCompare(speciesA) || b.level - a.level;
      if (boxSortBy === 'dateDesc') return timeB - timeA;
      if (boxSortBy === 'dateAsc') return timeA - timeB;
      return 0;
  });

  const maxProduction = results.length > 0 ? results[0].production : 0;
  const validItemsCount = results.filter(r => r.production > 0).length;
  const tierCounts = { S: 0, A: 0, B: 0, C: 0, Zero: 0 };

  const resultsWithTiers = results.map((result, index) => {
      if (result.production === 0) {
          tierCounts.Zero++;
          return { ...result, tier: 'Zero' };
      }

      const percentile = validItemsCount > 0 ? index / validItemsCount : 0;
      let tier = 'C';
      if (percentile < 0.10) {
          tier = result.production >= (maxProduction * 0.5) ? 'S' : 'A';
      } else if (percentile < 0.35) {
          tier = 'A';
      } else if (percentile < 0.75) {
          tier = 'B';
      }
      tierCounts[tier]++;
      return { ...result, tier };
  });

  const renderQuickTargets = (isMobile = false) => (
      <div className={`flex flex-wrap items-center gap-1.5 ${isMobile ? 'flex-nowrap shrink-0' : 'pl-1 -mt-1'}`}>
          <span className={`text-[10px] font-black text-indigo-400 uppercase tracking-wider shrink-0 ${isMobile ? 'mr-1' : ''}`}>Quick Target:</span>
          
          {uniqueTargets.berries.map(berry => (
              <button key={`berry-${berry}`} className="bg-white border border-indigo-100 hover:bg-indigo-100 hover:border-indigo-300 text-indigo-700 p-0.5 rounded shadow-sm transition shrink-0" onClick={() => { setCategory('Berry'); setTarget(formatBerryTarget(berry)); }} title={`Target ${formatBerryTarget(berry)}`}>
                  <img src={BERRY_ICONS[berry]} className="w-4 h-4 drop-shadow-sm" alt=""/>
              </button>
          ))}

          {uniqueTargets.ingredients.map(({ name: ing, isLocked }) => (
              <button key={`ing-${ing}`} className={`bg-white border text-indigo-700 p-0.5 rounded shadow-sm transition shrink-0 ${isLocked ? 'border-gray-200 opacity-50 grayscale hover:grayscale-0 hover:opacity-100 hover:border-indigo-300 hover:bg-indigo-50' : 'border-indigo-100 hover:bg-indigo-100 hover:border-indigo-300'}`} onClick={() => { setCategory('Ingredient'); const ingTarget = getTargetForIngredient(ing); if (ingTarget) setTarget(ingTarget); }} title={`Target ${getTargetForIngredient(ing) || ing}${isLocked ? ' (Locked)' : ''}`}>
                  <img src={INGREDIENT_ICONS[ing]} className="w-4 h-4 drop-shadow-sm" alt=""/>
              </button>
          ))}

          {uniqueTargets.skills.map(skill => (
              <button key={`skill-${skill}`} className="bg-white border border-indigo-100 hover:bg-indigo-100 hover:border-indigo-300 text-indigo-700 p-0.5 rounded shadow-sm transition shrink-0" onClick={() => { setCategory('Skill'); const skillTarget = getTargetForSkill(skill); if (skillTarget) setTarget(skillTarget); }} title={`Target ${formatSkillName(skill)}`}>
                  <img src={MAIN_SKILL_ICONS[skill] || "/images/mainskill/metronome.png"} className="w-4 h-4 drop-shadow-sm" alt=""/>
              </button>
          ))}
      </div>
  );

  if (!isOpen) return null;

  return (
    <div 
      className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50"
      onClick={onClose}
    >
      <div 
        className="bg-white rounded-2xl shadow-2xl w-[95vw] md:w-11/12 max-w-4xl max-h-[90vh] overflow-hidden flex flex-col ring-1 ring-gray-900/5 relative"
        onClick={(e) => e.stopPropagation()} 
      >
        {/* DARK HEADER */}
        <div className="bg-gray-800 p-3 md:p-4 flex justify-between items-center text-white shrink-0 shadow-md z-40">
          <div className="flex items-center gap-2">
            <Activity size={20} className="text-indigo-400" />
            <h2 className="text-lg md:text-xl font-black tracking-tight">Benchmark</h2>
          </div>
          <button onClick={onClose} className="p-1.5 md:p-2 bg-gray-700 hover:bg-red-500 rounded-lg transition-colors text-gray-300 hover:text-white shadow-sm" aria-label="Close modal">
            <X size={20} />
          </button>
        </div>

      {/* MOBILE: Sticky Targets & Accordion */}
      <div className="md:hidden sticky top-0 z-50 bg-white border-b border-gray-200 p-2 shadow-md flex items-center overflow-x-auto no-scrollbar">{renderQuickTargets(true)}</div>
      <div className="md:hidden border-b border-gray-200 bg-white shrink-0"><button onClick={() => setIsSettingsOpen(!isSettingsOpen)} className="w-full p-3 flex justify-between items-center font-black uppercase tracking-widest text-indigo-600 text-xs bg-indigo-50/50"><span>Configuration</span><ChevronDown className={`transition-transform ${isSettingsOpen ? 'rotate-180' : ''}`} size={16} /></button></div>

      {/* SETTINGS CONTAINER (Accordion on Mobile, Static on Desktop) */}
      <div className={`${isSettingsOpen ? 'flex' : 'hidden'} md:flex flex-col gap-4 md:gap-3 bg-gray-50 p-4 md:p-3 border-b border-gray-200 shrink-0 relative z-30`} onClick={(e) => e.stopPropagation()}>
            {/* Row 1: Mode, Category, Target, Box Select */}
            <div className="flex flex-wrap items-center gap-2 md:gap-3">
                <div className="flex items-center bg-white rounded-lg border border-gray-200 overflow-hidden shadow-sm shrink-0">
                    <button
                        className={`px-3 py-1.5 text-xs font-bold transition-colors ${mode === 'all' ? 'bg-indigo-600 text-white' : 'text-gray-500 hover:bg-gray-50'}`}
                        onClick={() => setMode('all')}
                    >
                        Global
                    </button>
                    <div className="w-px h-4 bg-gray-200"></div>
                    <button
                        className={`px-3 py-1.5 text-xs font-bold transition-colors ${mode === 'box' ? 'bg-indigo-600 text-white' : 'text-gray-500 hover:bg-gray-50'}`}
                        onClick={() => setMode('box')}
                    >
                        My Box
                    </button>
                </div>

                <div className="w-px h-6 bg-gray-300 hidden md:block mx-1"></div>

                <div className="flex items-center gap-2 shrink-0">
                    <div className="relative">
                        <img src={getCategoryIcon(category)} className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 object-contain pointer-events-none drop-shadow-sm" alt="" />
                        <select 
                            className="pl-8 py-1.5 pr-6 border border-gray-200 rounded-lg bg-white focus:ring-2 focus:ring-indigo-500 outline-none text-xs font-bold text-gray-700 shadow-sm cursor-pointer"
                            value={category}
                            onChange={(e) => {
                                const newCat = e.target.value;
                                setCategory(newCat);
                                setTarget(TARGETS[newCat][0]);
                            }}
                        >
                            {CATEGORIES.map(cat => <option key={cat} value={cat}>{cat}</option>)}
                        </select>
                    </div>

                    <div className="relative">
                        <img src={getTargetIcon(category, target)} className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 object-contain pointer-events-none drop-shadow-sm" alt="" />
                        <select 
                            className="pl-8 py-1.5 pr-6 border border-gray-200 rounded-lg bg-white focus:ring-2 focus:ring-indigo-500 outline-none text-xs font-bold text-gray-700 shadow-sm cursor-pointer max-w-[150px] md:max-w-[200px] truncate"
                            value={target}
                            onChange={(e) => setTarget(e.target.value)}
                        >
                            {TARGETS[category].map(item => <option key={item} value={item}>{item}</option>)}
                        </select>
                    </div>
                </div>

                <div className="w-px h-6 bg-gray-300 hidden md:block mx-1"></div>

                <div className="flex items-center gap-2 shrink-0">
                    <span className="text-[10px] font-black text-gray-400 uppercase tracking-widest hidden sm:inline">{mode === 'box' ? 'Filter Box:' : 'Include Box:'}</span>
                    <div className="relative" ref={boxSelectRef}>
                        <button 
                            onClick={() => setIsBoxSelectOpen(!isBoxSelectOpen)}
                            className="py-1.5 px-3 border border-indigo-200 rounded-lg bg-indigo-50 text-indigo-700 font-bold text-xs text-left flex justify-between items-center min-w-[110px] shadow-sm hover:bg-indigo-100 transition"
                        >
                            <span className="truncate">
                            {selectedBoxMons.length === 0 ? 'None' : `${selectedBoxMons.length} Selected`}
                            </span>
                            <span className="text-indigo-400 text-[10px] ml-2">▼</span>
                        </button>
                        {isBoxSelectOpen && (
                            <>
                            <div className="fixed inset-0 z-40 md:hidden bg-black/20 backdrop-blur-[2px]" onClick={(e) => { e.stopPropagation(); setIsBoxSelectOpen(false); }}></div>
                            <div className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 md:absolute md:top-full md:mt-1 md:right-0 md:left-auto md:translate-x-0 md:translate-y-0 w-[90vw] md:w-64 bg-white border border-gray-200 rounded-xl md:rounded-md shadow-2xl md:shadow-xl z-50 max-h-[80vh] md:max-h-60 overflow-y-auto custom-scrollbar">
                                <div className="p-2 border-b border-gray-100 bg-gray-50 flex justify-between items-center sticky top-0 z-10">
                                    <span className="text-[10px] font-bold text-gray-500 uppercase tracking-wider">Sort by</span>
                                    <select 
                                    className="text-[10px] font-bold text-indigo-600 bg-white border border-gray-200 rounded px-1 py-0.5 outline-none focus:ring-2 focus:ring-indigo-500 cursor-pointer"
                                        value={boxSortBy}
                                        onChange={(e) => setBoxSortBy(e.target.value)}
                                    >
                                        <option value="levelDesc">Level (▼)</option>
                                        <option value="levelAsc">Level (▲)</option>
                                        <option value="nameAsc">Name (A-Z)</option>
                                        <option value="nameDesc">Name (Z-A)</option>
                                        <option value="dateDesc">Newest</option>
                                        <option value="dateAsc">Oldest</option>
                                    </select>
                                </div>
                                <div 
                                    className={`px-3 py-2 cursor-pointer hover:bg-indigo-50 text-xs font-bold ${selectedBoxMons.length === 0 ? 'bg-indigo-100 text-indigo-700' : 'text-gray-700'}`}
                                    onClick={() => { setSelectedBoxMons([]); setIsBoxSelectOpen(false); }}
                                >
                                    None
                                </div>
                                {sortedBox.map(mon => {
                                    const species = pokedex[mon.species_id];
                                    if (!species) return null;
                                    const displayName = mon.nickname ? `${species.name} (${mon.nickname})` : species.name;
                                    const isSelected = selectedBoxMons.includes(mon.uuid);
                                    return (
                                    <div 
                                        key={mon.uuid} 
                                        className={`px-3 py-2 cursor-pointer hover:bg-indigo-50 text-xs flex items-center gap-2 ${isSelected ? 'bg-indigo-50 text-indigo-700 font-bold' : 'text-gray-700 font-medium'}`}
                                        onClick={() => {
                                        if (isSelected) setSelectedBoxMons(prev => prev.filter(id => id !== mon.uuid));
                                        else setSelectedBoxMons(prev => [...prev, mon.uuid]);
                                        }}
                                    >
                                        <input type="checkbox" readOnly checked={isSelected} className="rounded text-indigo-600 focus:ring-indigo-500 pointer-events-none" />
                                        <img src={`/images/pokemon/${species.id}.png`} className="w-4 h-4 object-contain" onError={(e) => { e.target.src = '/images/pokemon/unknown.png'; }} alt="" />
                                        <span className="truncate">Lv {mon.level} {displayName}</span>
                                    </div>
                                    );
                                })}
                            </div>
                            </>
                        )}
                    </div>
                </div>
          </div>

                <div className="hidden md:flex">
                    {renderQuickTargets(false)}
                </div>

            {/* Row 2: Sim Params */}
            <div className="flex flex-wrap items-center gap-2 md:gap-4 pt-2 border-t border-gray-200">
                <div className="flex items-center gap-1.5 bg-white px-2 py-1.5 rounded-lg border border-gray-200 shadow-sm shrink-0">
                    <span className="text-xs font-bold text-gray-600">Lv.</span>
                    <select 
                    className="bg-transparent text-xs font-bold outline-none focus:ring-2 focus:ring-indigo-500 rounded text-indigo-600 cursor-pointer"
                        value={baselineLevel}
                        onChange={(e) => setBaselineLevel(Number(e.target.value))}
                    >
                        <option value={1}>1</option>
                        <option value={25}>25</option>
                        <option value={30}>30</option>
                        <option value={50}>50</option>
                        <option value={60}>60</option>
                        <option value={65}>65</option>
                    </select>
                </div>

                <div className="flex items-center bg-white rounded-lg border border-gray-200 overflow-hidden shadow-sm shrink-0">
                    <button onClick={() => setInventoryMode('unlimited')} className={`px-2.5 py-1.5 text-xs font-bold transition-colors ${inventoryMode === 'unlimited' ? 'bg-indigo-600 text-white' : 'text-gray-500 hover:bg-gray-50'}`}>Active</button>
                    <div className="w-px h-4 bg-gray-200"></div>
                    <button onClick={() => setInventoryMode('capped')} className={`px-2.5 py-1.5 text-xs font-bold transition-colors ${inventoryMode === 'capped' ? 'bg-indigo-900 text-white' : 'text-gray-500 hover:bg-gray-50'}`}>Sleep</button>
                </div>

            <select value={energyStrategy} onChange={(e) => setEnergyStrategy(e.target.value)} className="bg-white border border-gray-200 rounded-lg px-2 py-1.5 text-xs font-bold outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-gray-700 shadow-sm cursor-pointer shrink-0">
                    <option value="maintained">High Energy (E4E)</option>
                    <option value="natural">Natural Decay</option>
                    <option value="zero">Zero Energy</option>
                </select>

                <div className="hidden md:block flex-grow"></div>

                <div className="flex flex-wrap items-center gap-2 md:gap-3 w-full md:w-auto mt-1 md:mt-0">
                    <label className="flex items-center gap-1.5 cursor-pointer hover:bg-gray-200 p-1 rounded transition" title="Show S/A/B/C tier bands">
                        <input type="checkbox" className="w-4 h-4 text-indigo-600 accent-indigo-600 rounded border-gray-300 focus:ring-indigo-500" checked={showTiers} onChange={(e) => setShowTiers(e.target.checked)} />
                        <span className="text-xs font-bold text-gray-700 flex items-center gap-1"><List size={14} className="text-indigo-500" /> Show Tiers</span>
                    </label>

                    {mode === 'all' && (
                        <label className="flex items-center gap-1.5 cursor-pointer hover:bg-gray-200 p-1 rounded transition" title="Generate Perfect Stats to see maximum potential">
                            <input type="checkbox" className="w-4 h-4 text-indigo-600 accent-indigo-600 rounded border-gray-300 focus:ring-indigo-500" checked={isGodRoll} onChange={(e) => setIsGodRoll(e.target.checked)} />
                            <span className="text-xs font-bold text-gray-700 flex items-center gap-1"><Crown size={14} className="text-indigo-500" /> Perfect Stats</span>
                        </label>
                    )}

                    {(selectedBoxMons.length > 0 || mode === 'box') && (
                        <>
                            <label className="flex items-center gap-1.5 cursor-pointer hover:bg-gray-200 p-1 rounded transition" title="Evaluate Box Pokémon as if they had neutral natures and zero subskills.">
                                <input type="checkbox" className="w-4 h-4 text-indigo-600 accent-indigo-600 rounded border-gray-300 focus:ring-indigo-500" checked={ignoreSkills} onChange={(e) => setIgnoreSkills(e.target.checked)} />
                                <span className="text-xs font-bold text-gray-700">Ignore Skills</span>
                            </label>

                            <label className="flex items-center gap-1.5 cursor-pointer hover:bg-gray-200 p-1 rounded transition" title="Evaluate Box Pokémon at the specified Baseline Level instead of their actual level.">
                                <input type="checkbox" className="w-4 h-4 text-indigo-600 accent-indigo-600 rounded border-gray-300 focus:ring-indigo-500" checked={scaleToBaseline} onChange={(e) => setScaleToBaseline(e.target.checked)} />
                                <span className="text-xs font-bold text-gray-700">Scale Level</span>
                            </label>

                            <div className="flex items-center gap-1.5 hover:bg-gray-200 p-1 rounded transition">
                                <label className="flex items-center gap-1.5 cursor-pointer">
                                    <input type="checkbox" className="w-4 h-4 text-indigo-600 accent-indigo-600 rounded border-gray-300 focus:ring-indigo-500" checked={simulateEvolution} onChange={(e) => setSimulateEvolution(e.target.checked)} />
                                    <span className="text-xs font-bold text-gray-700">Evolved</span>
                                </label>
                                {simulateEvolution && selectedBoxMons.length > 0 && canEvolve && possibleEvolutions.length > 1 && (
                                    <select
                                    className="p-1 ml-1 border border-indigo-200 rounded text-[10px] font-bold bg-indigo-50 text-indigo-700 outline-none focus:ring-2 focus:ring-indigo-500 max-w-[100px] truncate"
                                        value={evolutionTarget}
                                        onChange={(e) => setEvolutionTarget(e.target.value)}
                                    >
                                        {possibleEvolutions.map(evo => (
                                            <option key={evo.id} value={evo.id}>{evo.name}</option>
                                        ))}
                                    </select>
                                )}
                            </div>
                        </>
                    )}
                </div>
            </div>
          </div>

        {/* SCROLLABLE BODY */}
        <div className="p-4 md:p-6 overflow-y-auto custom-scrollbar flex flex-col gap-3 md:gap-4 bg-gray-50 flex-1">
        <div className="bg-blue-50 text-blue-800 text-[10px] md:text-xs font-medium p-2.5 md:p-3 rounded-lg mb-4 border border-blue-100 flex gap-2 items-start">
          <span className="mt-0.5">💡</span>
          <p>
              {mode === 'box' 
                  ? <strong>Note:</strong> 
                  : <strong>Note:</strong>} 
              {mode === 'box' 
                  ? " Ranking all Pokémon in your box against each other. Options like 'Scale Level' and 'Evolve' apply to your entire box in this mode!" 
                  : ' Baseline Pokémon are calculated at your chosen level. "Perfect Stats" assumes a perfect nature and subskills for the chosen category.'}
          </p>
        </div>

        <div className="flex-1 bg-white border border-gray-200 rounded-xl overflow-hidden flex flex-col min-h-[400px]">
          <div className="bg-gray-200 px-3 md:px-4 py-2 flex justify-between items-center font-bold text-[10px] md:text-xs text-gray-600 uppercase tracking-wider">
            <span>Pokémon</span>
            <span>Daily Production</span>
          </div>
          
          <div className="flex-1 overflow-y-auto p-3 space-y-2 custom-scrollbar">
            {resultsWithTiers.length > 0 ? (
              resultsWithTiers.map((result, index) => {
                if (!result.display) return null; 

                const bgClass = result.isInjected 
                  ? "bg-indigo-50 border-indigo-400 shadow-md transform scale-[1.02] z-10 relative" 
                  : "bg-white border-gray-100 shadow-sm";
                
                const showDivider = showTiers && (index === 0 || result.tier !== resultsWithTiers[index - 1].tier);

                return (
                  <React.Fragment key={result.uniqueKey}>
                    {showDivider && (
                        <div className={`px-4 py-1.5 rounded-lg text-xs font-black uppercase tracking-widest flex items-center justify-between border shadow-sm ${
                            result.tier === 'S' ? 'bg-yellow-50 text-yellow-600 border-yellow-200' :
                            result.tier === 'A' ? 'bg-blue-50 text-blue-600 border-blue-200' :
                            result.tier === 'B' ? 'bg-green-50 text-green-600 border-green-200' :
                            result.tier === 'C' ? 'bg-gray-100 text-gray-500 border-gray-200' :
                            'bg-gray-50 text-gray-400 border-gray-200'
                        }`}>
                            <span>{result.tier === 'Zero' ? `Does not produce ${target}` : `${result.tier} Tier`}</span>
                            <span>{tierCounts[result.tier]} Pokémon</span>
                        </div>
                    )}
                    <div className={`p-2 md:p-3 rounded-lg border flex items-center justify-between transition-transform hover:-translate-y-0.5 ${bgClass}`}>
                    <div className="flex items-center gap-2 md:gap-4 min-w-0">
                      <div className="font-black text-gray-400 w-4 md:w-6 text-right text-xs md:text-base shrink-0">#{index + 1}</div>
                      <img 
                        src={`/images/pokemon/${result.id}${result.is_shiny ? '_shiny' : ''}.png`} 
                        alt={result.name}
                        className="w-10 h-10 md:w-12 md:h-12 object-contain drop-shadow-md shrink-0"
                        onError={(e) => { e.target.src = '/images/pokemon/unknown.png'; }}
                      />
                      
                      <div className="flex flex-col min-w-0">
                        <div className="font-bold text-gray-800 text-base md:text-lg flex flex-wrap items-center gap-1 md:gap-2 leading-tight md:leading-none mb-1">
                          <span className="truncate">{result.name}</span>
                          {result.is_shiny && <Sparkles size={14} className="text-indigo-600 fill-indigo-100 shrink-0" />}
                          {result.isInjected && (
                            <span className="bg-indigo-600 text-white text-[8px] md:text-[10px] uppercase font-black px-1.5 md:px-2 py-0.5 rounded-full tracking-wider shrink-0 mt-0.5 md:mt-0">
                              {mode === 'box' ? 'Selected' : 'Included'}
                            </span>
                          )}
                        </div>

                        <div className="flex flex-col gap-1">
                            <div className="flex flex-wrap items-center gap-1 md:gap-2 text-[9px] md:text-[10px] font-bold">
                                <span className="text-indigo-600 bg-indigo-100/50 border border-indigo-200 px-1.5 py-0.5 rounded">
                                    Lv {result.display.level} {result.display.isScaled ? "(Scaled)" : ""}
                                </span>
                                
                                <div className="flex gap-1 items-center bg-gray-50 px-1.5 py-0.5 rounded border border-gray-200 shadow-sm">
                                   <span className="text-gray-500">{result.display.nature}</span>
                                   {NATURES[result.display.nature]?.up && <span className="text-red-500">+{NATURES[result.display.nature].up}</span>}
                                   {NATURES[result.display.nature]?.down && <span className="text-blue-400">-{NATURES[result.display.nature].down}</span>}
                                </div>

                                {result.display.ingredients && (
                                    <div className="flex gap-1 ml-1">
                                        {["0", "30", "60"].map((lvl) => {
                                            const ing = result.display.ingredients[lvl];
                                            if (!ing) return null;
                                            const isLocked = !result.display.forceUnlock && result.display.level < parseInt(lvl);
                                            if (isLocked) return null; 
                                            return (
                                                <div key={lvl} className={`flex items-center gap-0.5 bg-white border border-gray-200 px-1 py-0.5 rounded shadow-sm text-[7px] md:text-[8px] font-black text-gray-600`}>
                                                    <img src={INGREDIENT_ICONS[ing.name]} className="w-3 h-3" alt=""/> x{ing.count}
                                                </div>
                                            );
                                        })}
                                    </div>
                                )}
                                
                                {result.display.label && (
                                     <span className={`${result.display.isValid ? 'text-gray-400' : 'text-red-400'} ml-1`}>
                                       {result.display.label}
                                     </span>
                                )}
                            </div>

                            {renderSubskills(result.display.subskills, result.display.level, result.display.forceUnlock)}
                        </div>
                      </div>
                      
                    </div>
                    <div className={`font-black text-lg md:text-xl ml-2 shrink-0 ${result.isInjected ? 'text-indigo-800' : 'text-indigo-600'}`}>
                      {result.production.toFixed(2)}
                    </div>
                  </div>
                  </React.Fragment>
                );
              })
            ) : (
              <div className="flex flex-col items-center justify-center h-full text-gray-400 font-bold gap-2">
                <span className="text-4xl">🔍</span>
                <span>No Pokémon found that produce this {category.toLowerCase()}.</span>
              </div>
            )}
          </div>
        </div>

        </div>
      </div>
    </div>
  );
}